import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
} from "@mui/material";
import { Formik } from "formik";
import { ReactElement, useState } from "react";
import * as Yup from "yup";

import {
  CreateSuppressionsFromListMutation,
  CreateSuppressionsFromListMutationVariables,
  TrackedJobStatus,
  UploadType,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import Loading from "~frontend/components/Loading";
import RouteLeavingGuard from "~frontend/components/RouteLeavingGuard";
import { useCsvUpload } from "~frontend/components/forms/CsvUpload/useCsvUpload";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import { CREATE_SUPPRESSIONS_FROM_LIST } from "~frontend/graphql/campaigns";
import { CampaignWithSuppressionData } from "~frontend/routes/types";
import theme from "~frontend/theme";
import { jobFailed } from "~frontend/util/jobs";
import useMutation from "~frontend/util/useMutation";
import { useStaffCsvFilenameValidator } from "~frontend/util/useStaffCsvFilenameValidator";

import JobComplete from "./JobComplete";

export default function SuppressionCsvUpload({
  campaign,
  onCloseModal,
  onApplySuppressions,
}: {
  campaign: CampaignWithSuppressionData;
  onCloseModal: () => void;
  onApplySuppressions: () => void;
}): ReactElement {
  const [overwritingCsv, setOverwritingCsv] = useState(false);

  const [startImport, { loading: startingImport }] = useMutation<
    CreateSuppressionsFromListMutation,
    CreateSuppressionsFromListMutationVariables
  >(CREATE_SUPPRESSIONS_FROM_LIST, {
    onErrorSnackbarMessage: "Error starting import",
  });

  const [importError, setImportError] = useState<string | null>(null);

  const validator = useStaffCsvFilenameValidator();

  const {
    renderCsvUpload,
    csvState,
    startCsvUpload,
    setProcessingState,
    isOverwritingInProgress: isCsvDirty,
  } = useCsvUpload({
    allowPhoneNumberOnlyColumn: true,
    uploadType: UploadType.OptOutList,
    overwriting: overwritingCsv,
    onChangeOverwriting: setOverwritingCsv,
    doNotRenderFileWhileImporting: true,
    renderProcessingState: () => {
      switch (csvState) {
        case "CSV_IMPORT_FAILED":
          return (
            <Alert severity="error">
              {importError || "Error importing file. Please try again."}
            </Alert>
          );
        case "IMPORTING_CSV": {
          if (startingImport) {
            return <Loading message="Suppressing Contacts" />;
          } else if (
            campaign.createSuppressionsFromListJob &&
            jobFailed(campaign.createSuppressionsFromListJob).failed
          ) {
            return (
              <Alert severity="error">
                {campaign.createSuppressionsFromListJob?.errorMessage ||
                  "Error importing file. Please try again."}
              </Alert>
            );
          } else if (
            campaign.createSuppressionsFromListJob?.status ===
            TrackedJobStatus.Done
          ) {
            return (
              <JobComplete
                job={campaign.createSuppressionsFromListJob}
                campaign={campaign}
              />
            );
          } else {
            return (
              <Loading
                message="Suppressing Contacts"
                progress={
                  campaign.createSuppressionsFromListJob
                    ? campaign.createSuppressionsFromListJob.progress / 100
                    : undefined
                }
              />
            );
          }
        }
      }

      return <></>;
    },
    validCsvFilename: validator,
  });

  const isUploading = csvState === "UPLOADING_CSV";
  const jobRunningOrComplete = csvState === "IMPORTING_CSV";

  return (
    <Formik
      initialValues={{
        phoneNumberColumn: "",
      }}
      validationSchema={Yup.object().shape({
        phoneNumberColumn: Yup.string().required("Required"),
      })}
      onSubmit={async (values, { resetForm, setSubmitting }) => {
        setImportError(null);

        const uploadMetadata = await startCsvUpload({
          phoneNumberColumn: values.phoneNumberColumn,
        });

        const handleError = (message: string) => {
          setSubmitting(false);
          setProcessingState("CSV_IMPORT_FAILED");
          setImportError(message);
          setOverwritingCsv(true);
        };

        if (uploadMetadata) {
          setProcessingState("IMPORTING_CSV");

          const { errors } = await startImport({
            variables: {
              input: {
                campaignId: campaign.id,
                estimatedRowCount: uploadMetadata.estimatedRowCount,
                uploadToken: uploadMetadata.uploadToken,
                phoneNumberColumn: values.phoneNumberColumn,
              },
            },
          });

          if (errors) {
            handleError("Error importing suppressions. Please try again.");
            return;
          }

          onApplySuppressions();
          resetForm();
        } else {
          handleError("Error uploading file. Please try again.");
        }
      }}
    >
      {({ dirty, submitForm, isSubmitting, isValid, setFieldValue }) => {
        const tooltipText = () => {
          if (csvState === "NO_CSV") {
            return "Please select a CSV";
          }

          if (!dirty || !isValid) {
            return "All fields must be filled out";
          }

          return null;
        };

        return (
          <>
            <RouteLeavingGuard when={isCsvDirty || dirty} />

            <DialogTitle>Upload Suppressions</DialogTitle>
            <DialogContent>
              <FlexBox flexDirection="column" gap={theme.spacing(1)}>
                {renderCsvUpload(setFieldValue)}
              </FlexBox>
            </DialogContent>
            {jobRunningOrComplete ? (
              <DialogActions>
                <Button
                  disabled={isUploading || isSubmitting}
                  onClick={onCloseModal}
                  variant="contained"
                >
                  Close
                </Button>
              </DialogActions>
            ) : (
              <DialogActions>
                <Button
                  disabled={isUploading || isSubmitting}
                  onClick={onCloseModal}
                  variant="contained"
                  color="tertiary"
                >
                  Cancel
                </Button>
                <FormikSubmitButton
                  disabled={!isValid || isUploading}
                  onClick={submitForm}
                  submitText="Upload"
                  loadingText="Uploading..."
                  tooltipText={tooltipText()}
                />
              </DialogActions>
            )}
          </>
        );
      }}
    </Formik>
  );
}
